import { element } from '../../../global/index.js';
import { UIBadge } from '../../../components/badge/ui-badge.js';
import {
    fontMetrics,
    FontFamily,
} from './vertical-label-align-plugin.helper.js';
import { adoptStyle } from '../../../global/ui-helpers.js';
import styles from './vertical-label-align-plugin.css';

/**
 * @memberof SharedComponents
 * @classdesc Class for correcting fonts vertical alignment.
 * @alias VerticalLabelAlignPlugin
 * @param {UIElement} target Target instance.
 * @implements {IComponentPlugin}
 */
class VerticalLabelAlignPlugin {
    constructor(target) {
        this.target = target;
    }

    /**
     * Adds the vertical alignment wrapper for the target children text nodes.
     * @private
     * @returns {Array<Node|HTMLSpanElement>}
     */
    getFormattedChildrenElements() {
        const childrenNodes = this.target.detachChildNodes();
        return [].map.call(childrenNodes, (node) => {
            if (
                node.nodeType === Node.TEXT_NODE &&
                node.nodeValue.trim().length > 0
            ) {
                return element`<span class="vertical-label-align__container">${node}</span>`;
            }

            return node;
        });
    }

    /**
     * Set the CSS variables based on the target font metrics.
     * @private
     * @param {Array<Node|HTMLSpanElement>} formattedChildrenElements
     */
    setCssVariables(formattedChildrenElements) {
        const fontFamily = getComputedStyle(this.target).fontFamily?.split(
            ','
        )[0];
        const { emSquare, capHeight, ascender, descender } =
            fontMetrics[fontFamily] || fontMetrics[FontFamily.Default];

        formattedChildrenElements.forEach((formattedChildrenElement) => {
            if (
                formattedChildrenElement.classList?.contains(
                    'vertical-label-align__container'
                )
            ) {
                formattedChildrenElement.style.setProperty(
                    '--parent-height',
                    `${this.target.offsetHeight}`
                );
                formattedChildrenElement.style.setProperty(
                    '--em-square',
                    `${emSquare}`
                );
                formattedChildrenElement.style.setProperty(
                    '--cap-height',
                    `${capHeight}`
                );
                formattedChildrenElement.style.setProperty(
                    '--ascender',
                    `${ascender}`
                );
                formattedChildrenElement.style.setProperty(
                    '--descender',
                    `${descender}`
                );
                formattedChildrenElement.style.setProperty(
                    '--font-size',
                    `${parseFloat(getComputedStyle(this.target).fontSize)}`
                );
            }
        });
    }

    /**
     * @inheritDoc
     */
    render() {
        const formattedChildrenElements = this.getFormattedChildrenElements();
        if (formattedChildrenElements.length > 0) {
            this.setCssVariables(formattedChildrenElements);
            this.target.append(...formattedChildrenElements);
        }
    }

    /**
     * @inheritDoc
     */
    hydrate() {}
}

UIBadge.registerPlugin('vertical-label-align', VerticalLabelAlignPlugin);

adoptStyle(styles, 'vertical-label-align-plugin').catch((err) =>
    console.error(err)
);

export { VerticalLabelAlignPlugin };
